import type { Designer } from "@wix/ambassador-picasso-editor-v1-designer/types";
import {
  Box,
  FormField,
  Loader,
  Tag,
  Text,
  TextButton,
} from "@wix/design-system";
import { useTranslation } from "@wix/fe-essentials-standalone";
import { TEXT_STANDARD_PRIMARY_COLOR } from "@wix/picasso-common";
import { Add, DrawSmall } from "@wix/wix-ui-icons-common";
import {
  type RefObject,
  type Dispatch,
  type SetStateAction,
  useCallback,
} from "react";
import { MAX_ATTACHMENTS } from "../../../consts";
import { VerticalDivider } from "../Common/VerticalDivider";
import { ImageInputArea } from "../ImageInputArea/ImageInputArea";
import { ActionButtons } from "./ActionButtons";
import { useMobile } from "../../../hooks/useMobile";

interface InputPanelProps {
  generatingProject: boolean;
  handleGenerateSiteClick: () => void;
  prompt: string;
  setPrompt: Dispatch<SetStateAction<string>>;
  onFileAttachment: (files: string[]) => void;
  fileInputRef: RefObject<HTMLInputElement>;
  attachmentPreviews: string[];
  handleFileAttachmentClick: () => void;
  selectedDesignStudio?: Designer;
  handleDesignStudioClick: () => void;
  setSelectedDesignStudio: Dispatch<SetStateAction<Designer | undefined>>;
  sizeMapByElementType: any;
  toggleMicrophone: () => void;
  isListening: boolean;
  isMicPermissionDenied: boolean;
  selectedDesignerName: string;
  isBudgetExceeded?: boolean;
}

export function InputPanel({
  generatingProject,
  handleGenerateSiteClick,
  prompt,
  setPrompt,
  onFileAttachment,
  fileInputRef,
  attachmentPreviews,
  handleFileAttachmentClick,
  selectedDesignStudio,
  handleDesignStudioClick,
  setSelectedDesignStudio,
  selectedDesignerName,
  sizeMapByElementType,
  toggleMicrophone,
  isListening,
  isMicPermissionDenied,
  isBudgetExceeded,
}: InputPanelProps) {
  const { t } = useTranslation();
  const isMobile = useMobile();

  const getChosenDesignerText = useCallback(() => {
    return isMobile
      ? t("landing.mobile.chosen_designer", {
          designerName: selectedDesignerName,
        })
      : t("landing.chosen_designer", {
          designerName: selectedDesignerName,
        });
  }, [t, isMobile, selectedDesignerName]);

  return (
    <Box
      width="100%"
      background="var(--wds-color-fill-surface-sunken)"
      borderRadius="12px"
      direction="vertical"
      boxShadow="0px var(--Shadow-shadow-y-200, 0px) var(--Shadow-shadow-blur-200, 18px) 0px var(--Shadow-shadow-fill-primary-overlay, rgba(0, 6, 36, 0.10)), 0px 6px 6px 0px var(--Shadow-shadow-fill-secondary-overlay, rgba(0, 6, 36, 0.00))"
    >
      <FormField>
        <ImageInputArea
          placeholder={t(
            isMobile
              ? "landing.mobile.input_placeholder"
              : "landing.input_placeholder",
          )}
          isLoading={generatingProject}
          handleGenerateSiteClick={handleGenerateSiteClick}
          prompt={prompt}
          onChange={setPrompt}
          onFileAttachment={onFileAttachment}
          ref={fileInputRef}
          isDisabled={isBudgetExceeded}
        />
      </FormField>
      <Box padding={3} align="space-between" width={"calc(100% - 24px)"}>
        <Box
          gap={isMobile ? 3 : 4}
          verticalAlign="middle"
          marginLeft={isMobile ? 0 : 2}
        >
          <TextButton
            size="small"
            disabled={
              attachmentPreviews.length >= MAX_ATTACHMENTS || isBudgetExceeded
            }
            onClick={handleFileAttachmentClick}
            skin="dark"
            prefixIcon={<Add />}
          >
            {t(isMobile ? "landing.mobile.attachment" : "landing.attachment")}
          </TextButton>
          <VerticalDivider />
          {selectedDesignStudio ? (
            <Tag
              className="selected-design-studio"
              thumb={<DrawSmall color={TEXT_STANDARD_PRIMARY_COLOR} />}
              id={selectedDesignStudio.id!}
              onClick={handleDesignStudioClick}
              onRemove={() => setSelectedDesignStudio(undefined)}
            >
              <Text size="small" ellipsis>
                {getChosenDesignerText()}
              </Text>
            </Tag>
          ) : (
            <TextButton
              onClick={handleDesignStudioClick}
              size="small"
              prefixIcon={<DrawSmall />}
              disabled={isBudgetExceeded}
            >
              {t(
                isMobile
                  ? "landing.mobile.choose_designer"
                  : "landing.choose_designer",
              )}
            </TextButton>
          )}
        </Box>
        <Box alignSelf="end">
          {generatingProject ? (
            <Loader size="small" />
          ) : (
            <ActionButtons
              sizeMapByElementType={sizeMapByElementType}
              toggleMicrophone={toggleMicrophone}
              isListening={isListening}
              handleGenerateSiteClick={handleGenerateSiteClick}
              prompt={prompt}
              generatingProject={generatingProject}
              isMicPermissionDenied={isMicPermissionDenied}
              isBudgetExceeded={isBudgetExceeded}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
